import React, { FormEvent, useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import useToggleState from '~/hooks/useToggleState';

import { syncBusiness } from '~/services/BusinessTraveller/BusinessTravellerService';

interface Props {
  businessId: string;
}

export default function BusinessOrderSyncButton(props: Props) {
  const { businessId } = props;

  const { enqueueSnackbar } = useSnackbar();
  const { isToggled: isModalOpen, toggleOn: openModal, toggleOff: closeModal } = useToggleState();

  const businessSyncMutation = useMutation({
    mutationFn: async (businessId: string) => {
      return await syncBusiness(businessId);
    },
    onSuccess: () => {
      enqueueSnackbar('Business synced successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error syncing business: ${error.message}`, { variant: 'error' });
    },
  });

  const handleSubmission = useCallback(async (event: FormEvent) => {
    event.preventDefault();
    businessSyncMutation.mutate(businessId);
    closeModal();
  }, []);

  return (
    <>
      <Button variant="outlined" color="primary" onClick={openModal}>
        Synchronize Orders
      </Button>
      <Dialog open={isModalOpen} onClose={closeModal} maxWidth="sm">
        <form onSubmit={handleSubmission}>
          <DialogTitle>Synchronize Business Orders</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>Synchronize all business orders to the Business Traveller service.</Typography>
            <Typography gutterBottom>
              Note: This will only trigger the sync. The sync will run in the background and can take 5-10 minutes to
              complete. You will not be notified when it is complete.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button disabled={businessSyncMutation.isPending} variant="text" onClick={closeModal}>
              Cancel
            </Button>
            <LoadingButton loading={businessSyncMutation.isPending} type="submit" variant="contained" color="primary">
              Synchronize
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
